/**
 * Notes API
 * CRUD operations for notes on contacts, companies, deals, projects
 */
import apiClient from './index'

export interface Note {
  id: string
  content: string
  content_html?: string
  contact_id?: string
  company_id?: string
  deal_id?: string
  project_id?: string
  created_by_user_id?: string
  created_by_name?: string
  is_pinned: boolean
  created_at: string
  updated_at: string
}

export interface CreateNoteData {
  content: string
  content_html?: string
  is_pinned?: boolean
}

export interface UpdateNoteData {
  content?: string
  content_html?: string
  is_pinned?: boolean
}

interface NotesResponse {
  data: Note[]
  next_cursor?: string
}

interface NoteResponse {
  data: Note
}

// Contact notes
export async function getContactNotes(contactId: string, limit = 50, cursor?: string): Promise<NotesResponse> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (cursor) params.append('cursor', cursor)
  const { data } = await apiClient.get<NotesResponse>(`/contacts/${contactId}/notes?${params}`)
  return data
}

export async function createContactNote(contactId: string, noteData: CreateNoteData): Promise<Note> {
  const { data } = await apiClient.post<NoteResponse>(`/contacts/${contactId}/notes`, noteData)
  return data.data
}

// Company notes
export async function getCompanyNotes(companyId: string, limit = 50, cursor?: string): Promise<NotesResponse> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (cursor) params.append('cursor', cursor)
  const { data } = await apiClient.get<NotesResponse>(`/companies/${companyId}/notes?${params}`)
  return data
}

export async function createCompanyNote(companyId: string, noteData: CreateNoteData): Promise<Note> {
  const { data } = await apiClient.post<NoteResponse>(`/companies/${companyId}/notes`, noteData)
  return data.data
}

// Deal notes
export async function getDealNotes(dealId: string, limit = 50, cursor?: string): Promise<NotesResponse> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (cursor) params.append('cursor', cursor)
  const { data } = await apiClient.get<NotesResponse>(`/deals/${dealId}/notes?${params}`)
  return data
}

export async function createDealNote(dealId: string, noteData: CreateNoteData): Promise<Note> {
  const { data } = await apiClient.post<NoteResponse>(`/deals/${dealId}/notes`, noteData)
  return data.data
}

// Project notes
export async function getProjectNotes(projectId: string, limit = 50, cursor?: string): Promise<NotesResponse> {
  const params = new URLSearchParams({ limit: limit.toString() })
  if (cursor) params.append('cursor', cursor)
  const { data } = await apiClient.get<NotesResponse>(`/projects/${projectId}/notes?${params}`)
  return data
}

export async function createProjectNote(projectId: string, noteData: CreateNoteData): Promise<Note> {
  const { data } = await apiClient.post<NoteResponse>(`/projects/${projectId}/notes`, noteData)
  return data.data
}

// Generic note operations
export async function getNote(noteId: string): Promise<Note> {
  const { data } = await apiClient.get<NoteResponse>(`/notes/${noteId}`)
  return data.data
}

export async function updateNote(noteId: string, noteData: UpdateNoteData): Promise<Note> {
  const { data } = await apiClient.put<NoteResponse>(`/notes/${noteId}`, noteData)
  return data.data
}

export async function deleteNote(noteId: string): Promise<void> {
  await apiClient.delete(`/notes/${noteId}`)
}

export async function toggleNotePin(noteId: string): Promise<Note> {
  const { data } = await apiClient.post<NoteResponse>(`/notes/${noteId}/pin`)
  return data.data
}
